import { HUD_DISPLAY_FY } from "@/data/manual-facts";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand } from "@/components/site/Blocks";
import { getStateFmr } from "@/lib/fmr.functions";
import { STATE_RATES } from "@/data/stateTaxRates";
import { CountyChoropleth } from "@/components/site/CountyChoropleth";
import { HudHealthIndicator } from "@/components/site/HudHealthIndicator";
import { buildHead } from "@/lib/seo-head";
import { HudFreshnessBadge } from "@/components/site/HudFreshnessBadge";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import section8MapHero from "@/assets/hero/hero-section8-engaging.jpg";


export const Route = createFileRoute("/section-8-investing-map")({
  head: () =>
    buildHead({
      title: "Section 8 Investing Map — HUD FMR | Simply Approved",
      description:
        "Explore HUD Fair Market Rents by state and county. Find the highest-cash-flow Section 8 markets in the US and pull instant DSCR loan quotes.",
      url: "/section-8-investing-map",
      keywords:
        "Section 8 investing map, HUD FMR map, Section 8 cash flow map, highest cash flow Section 8 markets, HUD Fair Market Rent by county, Section 8 rental investing tool, interactive HUD rent map, Section 8 county rent lookup, DSCR loan Section 8 map, best Section 8 markets 2026",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Section 8 Investing Map", url: "/section-8-investing-map" },
      ],
      service: { name: "Section 8 Investing Map Tool" },
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Section 8 Investing Map",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/section-8-investing-map",
          description:
            "Interactive map of HUD Fair Market Rents by state and county for Section 8 investors, with instant DSCR loan modeling.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: MapPage,
});

type Bedrooms = "studio" | "oneBr" | "twoBr" | "threeBr" | "fourBr";
const BR_LABEL: Record<Bedrooms, string> = {
  studio: "Studio",
  oneBr: "1 BR",
  twoBr: "2 BR",
  threeBr: "3 BR",
  fourBr: "4 BR",
};

function MapPage() {
  const [state, setState] = useState("GA");
  const [br, setBr] = useState<Bedrooms>("threeBr");
  const [sortDesc, setSortDesc] = useState(true);

  const query = useQuery({
    queryKey: ["stateFmr", state],
    queryFn: () => getStateFmr({ data: { state } }),
    enabled: state.length === 2,
    staleTime: 60 * 60 * 1000,
  });

  const sorted = useMemo(() => {
    const rows = query.data?.entries ?? [];
    return [...rows].sort((a, b) => (sortDesc ? b.rents[br] - a.rents[br] : a.rents[br] - b.rents[br]));
  }, [query.data, br, sortDesc]);

  const stats = useMemo(() => {
    const rents = (query.data?.entries ?? []).map((e) => e.rents[br]).filter((v) => v > 0);
    if (rents.length === 0) return { min: 0, max: 0, median: 0, avg: 0 };
    const sortedRents = [...rents].sort((a, b) => a - b);
    const median = sortedRents[Math.floor(sortedRents.length / 2)];
    const avg = rents.reduce((s, v) => s + v, 0) / rents.length;
    return { min: sortedRents[0], max: sortedRents[sortedRents.length - 1], median, avg };
  }, [query.data, br]);

  const heat = (v: number) => {
    if (stats.max === stats.min) return "hsl(var(--muted))";
    const t = (v - stats.min) / (stats.max - stats.min);
    // green-to-brand-gold gradient
    const hue = 140 - t * 100; // 140 (green) → 40 (gold)
    return `hsl(${hue}, 65%, 55%)`;
  };

  return (
    <SiteShell>
      <PageHero
        eyebrow="Section 8 investing"
        title={`${HUD_DISPLAY_FY} HUD FMR investing map`}
        subtitle="Explore Fair Market Rents by county across the United States. Find the highest-cash-flow Section 8 markets in seconds."
        image={{
          src: section8MapHero,
          alt: "Rental home at twilight with a glowing neighborhood map overlay marking nearby Section 8 rental locations",
        }}
      />
      <PageEssentials />

      <Section>
        <div className="mx-auto max-w-6xl">
          <div className="mb-4 flex flex-wrap items-start justify-end gap-3">
            <HudHealthIndicator />
          </div>
          <div className="mb-4">
            <HudFreshnessBadge />
          </div>
          <div className="grid gap-4 rounded-xl border border-border bg-card p-6 shadow-sm md:grid-cols-[2fr_1fr_auto]">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-foreground">State</span>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-base"
              >
                {STATE_RATES.map((r) => (
                  <option key={r.code} value={r.code}>{r.name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-foreground">Bedrooms</span>
              <select
                value={br}
                onChange={(e) => setBr(e.target.value as Bedrooms)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-base"
              >
                {(Object.keys(BR_LABEL) as Bedrooms[]).map((k) => (
                  <option key={k} value={k}>{BR_LABEL[k]}</option>
                ))}
              </select>
            </label>
            <button
              onClick={() => setSortDesc((v) => !v)}
              className="self-end rounded-md border border-border bg-background px-4 py-2.5 text-sm font-semibold text-foreground hover:border-brand"
            >
              Sort: {sortDesc ? "Highest FMR" : "Lowest FMR"}
            </button>
          </div>

          {query.isPending && <p className="mt-6 text-sm text-muted-foreground">Loading county-level FMR data…</p>}
          {query.error && (
            <p className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
              {(query.error as Error).message}
            </p>
          )}

          {query.data && (
            <>
              <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4">
                <MiniStat label={`Highest ${BR_LABEL[br]} FMR`} value={`$${stats.max.toLocaleString()}`} />
                <MiniStat label={`Median ${BR_LABEL[br]} FMR`} value={`$${stats.median.toLocaleString()}`} />
                <MiniStat label={`Average ${BR_LABEL[br]} FMR`} value={`$${Math.round(stats.avg).toLocaleString()}`} />
                <MiniStat label={`Lowest ${BR_LABEL[br]} FMR`} value={`$${stats.min.toLocaleString()}`} />
              </div>

              <div className="mt-6">
                <CountyChoropleth
                  stateCode={state}
                  values={Object.fromEntries(
                    (query.data.entries ?? []).map((e) => [e.fipsCode.slice(0, 5), e.rents[br]]),
                  )}
                  ariaLabel={`${state} county Fair Market Rents, ${BR_LABEL[br]}`}
                />
              </div>

              <div className="mt-6 grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {sorted.map((row) => (
                  <div
                    key={row.fipsCode}
                    className="flex flex-col justify-between rounded-lg border border-border bg-card p-4 shadow-sm"
                    style={{ borderLeft: `4px solid ${heat(row.rents[br])}` }}
                  >
                    <div className="text-sm font-semibold text-foreground">{row.countyName}</div>
                    {row.areaName && <div className="text-xs text-muted-foreground">{row.areaName}</div>}
                    <div className="mt-3 flex items-baseline justify-between">
                      <span className="font-display text-2xl font-bold text-foreground">
                        ${row.rents[br].toLocaleString()}
                      </span>
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{BR_LABEL[br]}/mo</span>
                    </div>
                    <div className="mt-2 grid grid-cols-5 gap-1 text-center text-[10px] text-muted-foreground">
                      {(Object.keys(BR_LABEL) as Bedrooms[]).map((k) => (
                        <div key={k} className={k === br ? "font-bold text-foreground" : ""}>
                          <div>{BR_LABEL[k]}</div>
                          <div>${row.rents[k].toLocaleString()}</div>
                        </div>
                      ))}
                    </div>
                    <a
                      href={`/dscr-calculator?s=${btoa(JSON.stringify({ mode: "section8", lookupType: "county", countyState: state, countyFips: row.fipsCode, bedrooms: br })).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")}`}
                      className="mt-3 rounded-md bg-brand px-3 py-1.5 text-center text-xs font-semibold text-primary-foreground hover:bg-brand/90"
                    >
                      Model DSCR →
                    </a>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-muted-foreground">
                Data: HUD User Fair Market Rents API, {query.data.year}. FMR is the 40th-percentile gross rent for standard-quality
                rental units. Actual contract rent must pass the local housing authority's rent reasonableness test.
              </p>
            </>
          )}
        </div>
      </Section>

      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand
        title="Found a market? Let's price the loan."
        body="Submit a complete application with address, rents, credit authorization, and supporting docs — we.ll model DSCR against the FMR and reply with an initial scenario review. Formal pricing follows underwriting."
      />
    <PageOutro />
      </SiteShell>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-foreground tabular-nums">{value}</div>
    </div>
  );
}
